import { memo, useCallback, useEffect, useState } from "react";
import {
    Box,
    Button,
    Chip,
    Divider,
    Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import { getLambCodeHistory, getOwners } from "../../api/index.js";
import Form from "../../components/Form.js";
import Spinner from "../../components/Spinner.js";
import useSelectStyles from "../../components/styles/Select.js";
import { useSnackbar } from "../../utils/index.js";

const formatDate = (date) => (date ? new Date(date).toLocaleDateString("el-GR") : "Δεν έχει οριστεί");

const formatOwner = (owner) => {
    if (!owner) return "Άγνωστη εκτροφή";
    return `${owner.code || ""} - ${owner.lastName || ""} ${owner.firstName || ""}`.trim();
};

const LambCodeHistory = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [owners, setOwners] = useState([]);
    const [results, setResults] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [searchValues, setSearchValues] = useState(null);

    const classes = useSelectStyles();
    const navigate = useNavigate();
    const { error } = useSnackbar();

    const fetchOwners = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getOwners();
            setOwners(response.owners || []);
        } catch (requestError) {
            error(requestError);
        } finally {
            setIsLoading(false);
        }
    }, [error]);

    useEffect(() => {
        fetchOwners();
    }, [fetchOwners]);

    const ownerOptions = owners.map((owner) => ({
        value: `${owner.lastName} ${owner.firstName} - ${owner.code}`,
        text: `${owner.lastName} ${owner.firstName} - ${owner.code}`,
    }));

    const searchFormContent = [
        {
            customType: "dropdown",
            id: "owner",
            items: ownerOptions,
            placeholder: "Εκτροφή",
            required: true,
            defaultValue: searchValues?.owner || "",
        },
        {
            customType: "input",
            id: "lambSlug",
            type: "text",
            placeholder: "4-ψήφιος Κωδικός Αμνού",
            required: true,
            value: searchValues?.lambSlug || "",
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Αναζήτηση",
        },
    ];

    const handleSearch = async (values) => {
        const code = String(values.lambSlug || "").trim();
        if (!/^\d{1,4}$/.test(code)) {
            error("Ο κωδικός αμνού πρέπει να αποτελείται από 1 έως 4 ψηφία");
            return;
        }

        const normalizedCode = code.padStart(4, "0");
        const owner = owners.find((candidate) => (
            `${candidate.lastName} ${candidate.firstName} - ${candidate.code}` === values.owner
        ));
        if (!owner) {
            error("Η εκτροφή δεν είναι έγκυρη");
            return;
        }

        setIsLoading(true);
        setSearchValues({ owner: values.owner, lambSlug: normalizedCode });
        try {
            const response = await getLambCodeHistory(owner._id, normalizedCode);
            if (response.success) {
                setResults(response.results || []);
                setHasSearched(true);
            } else {
                error(response.message);
            }
        } catch (requestError) {
            error(requestError);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Spinner open={isLoading} />
            <Box className={classes.outerBox}>
                <Box container flexDirection="column" className={classes.innerBox}>
                    <Typography variant="h5" sx={{ margin: "1rem" }}>
                        Ιστορικό Κωδικού Αμνού
                    </Typography>
                    <Typography variant="body2" sx={{ margin: "0 1rem 1rem", textAlign: "center" }}>
                        Αναζητήστε έναν κωδικό στην ενεργή και την αποδεσμευμένη ιστορία της επιλεγμένης εκτροφής.
                    </Typography>
                    <Form content={searchFormContent} onSubmit={handleSearch} toResetForm={false} />
                </Box>

                {hasSearched && results.length === 0 && (
                    <Typography variant="h6" sx={{ marginBottom: "2rem" }}>
                        Δεν βρέθηκε αμνός με αυτόν τον κωδικό στην επιλεγμένη εκτροφή.
                    </Typography>
                )}

                {results.map((result) => (
                    <Box
                        key={result.animalId}
                        sx={{
                            width: "90%",
                            maxWidth: "900px",
                            border: "1px solid",
                            borderColor: result.codeStatus === "active" ? "warning.main" : "success.main",
                            borderRadius: "6px",
                            padding: "1.25rem",
                            marginBottom: "1.5rem",
                            backgroundColor: "white",
                        }}
                    >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.75rem" }}>
                            <Typography variant="h6">
                                {`Κωδικός ${result.searchedCode} — Περίοδος ${result.season || "Άγνωστη"}`}
                            </Typography>
                            <Chip
                                color={result.codeStatus === "active" ? "warning" : "success"}
                                label={result.codeStatus === "active" ? "Ενεργός" : "Αποδεσμευμένος"}
                            />
                        </Box>

                        <Divider sx={{ margin: "1rem 0" }} />
                        <Typography>{`Ημερομηνία γέννησης: ${formatDate(result.birthDate)}`}</Typography>
                        <Typography>{`Κατάσταση αμνού: ${result.statusLabel || "Άγνωστη"}`}</Typography>
                        <Typography>{`Τρέχουσα εκτροφή: ${formatOwner(result.currentOwner)}`}</Typography>
                        <Typography>{`Τρέχων κωδικός αμνού: ${result.lambSlug || "—"}`}</Typography>
                        <Typography>{`Ιστορικός κωδικός αμνού: ${result.oldLambSlug || "—"}`}</Typography>
                        <Typography>{`Κωδικός γενεαλογικού βιβλίου: ${result.registrationCode || "—"}`}</Typography>
                        <Typography>{`Κωδικός εκτροφής: ${result.flockCode || "—"}`}</Typography>

                        <Typography variant="subtitle1" sx={{ fontWeight: "bold", marginTop: "1rem" }}>
                            Συνδεδεμένοι τοκετοί
                        </Typography>
                        {result.lambings.length === 0 && (
                            <Typography>Δεν βρέθηκε συνδεδεμένος τοκετός.</Typography>
                        )}
                        {result.lambings.map((lambing) => (
                            <Box
                                key={lambing.id}
                                sx={{ marginTop: "0.75rem", padding: "0.75rem", backgroundColor: "#f5f5f5", borderRadius: "4px" }}
                            >
                                <Typography>{`Ημερομηνία τοκετού: ${formatDate(lambing.lambingDate)}`}</Typography>
                                <Typography>{`Ημερομηνία λήξης: ${formatDate(lambing.weaningDate)}`}</Typography>
                                <Typography>{`Ιστορική εκτροφή τοκετού: ${formatOwner(lambing.owner)}`}</Typography>
                                <Typography>{`Μητέρα: ${lambing.damRegistrationCode || "—"}`}</Typography>
                            </Box>
                        ))}

                        {result.needsEndLambing && (
                            <Box sx={{ marginTop: "1rem", padding: "1rem", backgroundColor: "warning.light", borderRadius: "4px" }}>
                                <Typography sx={{ fontWeight: "bold", marginBottom: "0.75rem" }}>
                                    {`Ο κωδικός δεν έχει αποδεσμευτεί. Εκτελέστε λήξη περιόδου ${result.season || "τοκετών"}.`}
                                </Typography>
                                <Button variant="contained" color="warning" onClick={() => navigate("/lambings/lambs/end")}>
                                    Μετάβαση στη Λήξη Περιόδου Αμνών
                                </Button>
                            </Box>
                        )}
                    </Box>
                ))}
            </Box>
        </>
    );
};

export default memo(LambCodeHistory);
